import { apiFetch } from "../lib/api";
import { useState, useCallback } from "react";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Upload, FileText, Loader2, X, Check, AlertTriangle,
  UtensilsCrossed, Wine, CheckCircle2, ChevronRight, Search,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MenuImportDialog({ open, onClose, restaurantId, onImported, onRestaurantNeeded, onConfirmPreview }) {
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=processing
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // AI response
  const [importId, setImportId] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [menuName, setMenuName] = useState("Menu Importado");
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [newRestName, setNewRestName] = useState("");
  const [newRestType, setNewRestType] = useState("restaurant");

  const reset = useCallback(() => {
    setStep(1); setFile(null); setUploading(false); setPreview(null);
    setImportId(null); setSelectedItems(new Set()); setMenuName("Menu Importado");
    setConfirming(false); setResult(null); setFilterType("all"); setSearch("");
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Formato no soportado. Usa PDF, JPG o PNG.");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (max 20MB)");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (restaurantId) formData.append("restaurant_id", restaurantId);

      const resp = await apiFetch(`${API}/api/ai/import-menu-file`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${resp.status}`);
      }

      const data = await resp.json();
      setImportId(data.import_id);
      toast.success(`Archivo recibido (${data.pages} pagina(s)). Analizando con IA...`);

      // Poll for AI analysis completion
      let pollCount = 0;
      const poll = setInterval(async () => {
        pollCount += 1;
        try {
          const statusResp = await apiFetch(`${API}/api/ai/import-status/${data.import_id}`, { credentials: "include" });
          const statusData = await statusResp.json();

          if (statusData.status === "preview" && statusData.items) {
            clearInterval(poll);
            setPreview(statusData);
            setSelectedItems(new Set(statusData.items.map((_, i) => i)));
            if (statusData.restaurant_name) {
              setMenuName(statusData.restaurant_name);
              setNewRestName(prev => prev || statusData.restaurant_name);
            }
            setStep(2);
            setUploading(false);
            toast.success(`Encontrados ${statusData.total_items} items`);
          } else if (statusData.status === "failed") {
            clearInterval(poll);
            setUploading(false);
            toast.error("Error en el analisis: " + (statusData.error || "Intenta de nuevo"));
          }
          // Every ~30s during long waits, give the user a progress heartbeat
          if (pollCount === 20) toast.info("Sigue en curso. Los menús grandes pueden tardar 1-3 minutos…", { duration: 4000 });
          if (pollCount === 60) toast.info("Menú muy extenso — seguimos procesando, ya casi está.", { duration: 4000 });
        } catch { /* ignore polling errors */ }
      }, 3000);

      // Extended timeout: 6 minutes (was 3). Gemini multi-page extraction
      // can take 3-5 min on large menus + worker queue backlog in prod.
      setTimeout(() => {
        clearInterval(poll);
        if (step === 1) {
          setUploading(false);
          toast.error(
            "El análisis está tardando más de lo habitual. El archivo se está procesando en segundo plano — vuelve a subirlo en unos minutos o revisa la bandeja.",
            { duration: 8000 },
          );
        }
      }, 360000);

    } catch (err) {
      toast.error(err.message || "Error procesando archivo");
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedItems.size === 0) return;

    const items = preview.items.filter((_, i) => selectedItems.has(i));
    const food = items.filter(i => i.type !== "beverage");
    const bev = items.filter(i => i.type === "beverage");

    // Preview-only mode: return data to parent (for Onboarding)
    if (onConfirmPreview) {
      onConfirmPreview({
        import_id: importId,
        items,
        total_food: food.length,
        total_beverages: bev.length,
        menu_name: menuName,
      });
      return;
    }

    // Full mode: create directly via backend
    let rid = restaurantId;
    if (!rid && onRestaurantNeeded) {
      if (!newRestName.trim()) { toast.error("Introduce el nombre de tu negocio"); return; }
      rid = await onRestaurantNeeded(newRestName.trim(), newRestType);
      if (!rid) { toast.error("Error al crear el restaurante"); return; }
    }
    if (!rid) { toast.error("No hay restaurante asociado"); return; }

    setConfirming(true);
    try {
      const items = preview.items.filter((_, i) => selectedItems.has(i));
      const resp = await apiFetch(`${API}/api/ai/confirm-menu-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          import_id: importId,
          restaurant_id: rid,
          menu_name: menuName,
          items,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${resp.status}`);
      }
      const data = await resp.json();
      setResult(data);
      setStep(3);

      // Poll for completion
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const status = await apiFetch(`${API}/api/ai/import-status/${importId}`, { credentials: "include" });
          const statusData = await status.json();
          if (statusData.status === "completed") {
            clearInterval(poll);
            setResult(statusData);
            toast.success(`Menu creado: ${statusData.dishes_created} platos + ${statusData.beverages_created} bebidas`);
            onImported?.();
          } else if (statusData.status === "failed") {
            clearInterval(poll);
            toast.error("Error al crear el menu: " + (statusData.error || ""));
          }
        } catch { /* ignore */ }
        if (attempts > 30) clearInterval(poll);
      }, 2000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirming(false);
    }
  };

  const toggleItem = (idx) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectAllFood = () => {
    const idxs = preview.items.map((item, i) => item.type !== "beverage" ? i : null).filter(i => i !== null);
    setSelectedItems(new Set([...selectedItems, ...idxs]));
  };
  const selectAllBev = () => {
    const idxs = preview.items.map((item, i) => item.type === "beverage" ? i : null).filter(i => i !== null);
    setSelectedItems(new Set([...selectedItems, ...idxs]));
  };
  const selectAll = () => setSelectedItems(new Set(preview.items.map((_, i) => i)));
  const selectNone = () => setSelectedItems(new Set());

  if (!open) return null;

  const filteredItems = (preview?.items || [])
    .map((item, origIdx) => ({ ...item, origIdx }))
    .filter(item => {
      if (filterType === "food" && item.type === "beverage") return false;
      if (filterType === "beverage" && item.type !== "beverage") return false;
      if (search.trim() && !item.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });

  const selectedFood = preview ? preview.items.filter((it, i) => selectedItems.has(i) && it.type !== "beverage").length : 0;
  const selectedBev = preview ? preview.items.filter((it, i) => selectedItems.has(i) && it.type === "beverage").length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="menu-import-dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-bold">Importar Menu desde Archivo</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {step === 1 && "Sube un PDF o imagen de tu menu"}
              {step === 2 && `${preview?.total_items || 0} items encontrados — selecciona los que quieres importar`}
              {step === 3 && "Creando menu y platos..."}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="px-5 py-3 border-b border-border flex gap-2">
          {[
            { n: 1, label: "Subir archivo" },
            { n: 2, label: "Vista previa" },
            { n: 3, label: "Confirmacion" },
          ].map(s => (
            <div key={s.n} className={`flex items-center gap-1.5 text-xs font-medium ${step >= s.n ? "text-primary" : "text-muted-foreground"}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step > s.n ? "bg-primary text-primary-foreground" : step === s.n ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
              }`}>
                {step > s.n ? <Check className="w-3 h-3" /> : s.n}
              </div>
              <span className="hidden sm:inline">{s.label}</span>
              {s.n < 3 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  file ? "border-primary/50 bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}
                data-testid="file-dropzone"
              >
                {file ? (
                  <div className="space-y-2">
                    <FileText className="w-10 h-10 text-primary mx-auto" />
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB — {file.type}</p>
                    <Button variant="ghost" size="sm" onClick={() => setFile(null)}>Cambiar archivo</Button>
                  </div>
                ) : (
                  <label className="cursor-pointer space-y-3 block">
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto" />
                    <p className="text-sm text-muted-foreground">
                      Arrastra o haz click para seleccionar
                    </p>
                    <p className="text-xs text-muted-foreground/60">PDF, JPG o PNG (max 20MB)</p>
                    <input type="file" className="hidden" accept=".pdf,image/jpeg,image/png,image/webp"
                      onChange={handleFileSelect} data-testid="file-input" />
                  </label>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">La IA analizara el archivo completo:</p>
                <p>• Si es un PDF de varias paginas, lee TODAS las paginas</p>
                <p>• Detecta platos, bebidas, precios y categorias automaticamente</p>
                <p>• Podras revisar y seleccionar antes de confirmar</p>
              </div>
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 2 && preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex gap-3">
                <div className="flex-1 bg-primary/10 rounded-lg p-3 text-center">
                  <UtensilsCrossed className="w-5 h-5 text-primary mx-auto mb-1" />
                  <p className="text-lg font-bold">{preview.total_food}</p>
                  <p className="text-[10px] text-muted-foreground">Platos</p>
                </div>
                <div className="flex-1 bg-cyan-500/10 rounded-lg p-3 text-center">
                  <Wine className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
                  <p className="text-lg font-bold">{preview.total_beverages}</p>
                  <p className="text-[10px] text-muted-foreground">Bebidas</p>
                </div>
                <div className="flex-1 bg-muted rounded-lg p-3 text-center">
                  <FileText className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
                  <p className="text-lg font-bold">{preview.pages}</p>
                  <p className="text-[10px] text-muted-foreground">Paginas</p>
                </div>
              </div>

              {/* Menu name */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Nombre del menu</label>
                <Input value={menuName} onChange={e => setMenuName(e.target.value)} data-testid="menu-name-input" />
              </div>

              {/* New restaurant fields (only in full mode without restaurant) */}
              {!restaurantId && onRestaurantNeeded && !onConfirmPreview && (
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
                  <p className="text-xs font-semibold text-primary">Se creara tu restaurante automaticamente</p>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Nombre del negocio *</label>
                    <Input value={newRestName} onChange={e => setNewRestName(e.target.value)} placeholder="Ej: La Tasca de Juan" data-testid="new-rest-name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Tipo de negocio</label>
                    <select value={newRestType} onChange={e => setNewRestType(e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="new-rest-type">
                      <option value="restaurant">Restaurante</option>
                      <option value="bar">Bar</option>
                      <option value="cafe">Cafeteria</option>
                      <option value="food_truck">Food Truck</option>
                      <option value="hotel_restaurant">Hotel Restaurante</option>
                      <option value="catering">Catering</option>
                      <option value="bakery">Panaderia</option>
                      <option value="other">Otro</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex bg-muted rounded-lg p-0.5">
                  {[
                    { key: "all", label: `Todos (${preview.items.length})` },
                    { key: "food", label: `Platos (${preview.total_food})` },
                    { key: "beverage", label: `Bebidas (${preview.total_beverages})` },
                  ].map(f => (
                    <button key={f.key} onClick={() => setFilterType(f.key)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        filterType === f.key ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"
                      }`}>{f.label}</button>
                  ))}
                </div>
                <div className="relative flex-1 min-w-[120px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar..." className="w-full bg-muted border-0 text-sm rounded-lg pl-8 pr-3 py-1.5" />
                </div>
              </div>

              {/* Quick selection */}
              <div className="flex gap-2 text-[11px]">
                <button onClick={selectAll} className="text-primary hover:underline">Todos</button>
                <span className="text-muted-foreground/40">|</span>
                <button onClick={selectNone} className="text-muted-foreground hover:underline">Ninguno</button>
                <span className="text-muted-foreground/40">|</span>
                <button onClick={selectAllFood} className="text-primary hover:underline">Solo platos</button>
                <span className="text-muted-foreground/40">|</span>
                <button onClick={selectAllBev} className="text-cyan-500 hover:underline">Solo bebidas</button>
              </div>

              {/* Items list */}
              <ScrollArea className="max-h-[300px]">
                <div className="space-y-1">
                  {filteredItems.map(item => {
                    const isSelected = selectedItems.has(item.origIdx);
                    const isBev = item.type === "beverage";
                    return (
                      <button
                        key={item.origIdx}
                        onClick={() => toggleItem(item.origIdx)}
                        data-testid={`import-item-${item.origIdx}`}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                          isSelected ? "border-primary/40 bg-primary/5" : "border-transparent hover:bg-muted/50"
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                        }`}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium truncate">{item.name}</span>
                            <Badge variant="outline" className={`text-[8px] px-1 py-0 ${
                              isBev ? "border-cyan-500/30 text-cyan-500" : "border-primary/30 text-primary"
                            }`}>{isBev ? "Bebida" : "Plato"}</Badge>
                          </div>
                          {item.description && <p className="text-[10px] text-muted-foreground truncate">{typeof item.description === "string" ? item.description : ""}</p>}
                          <p className="text-[10px] text-muted-foreground">{item.category}</p>
                        </div>
                        <span className="text-sm font-bold text-primary flex-shrink-0">
                          {item.price ? `${Number(item.price).toFixed(2)}\u20AC` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Selection summary */}
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Seleccionados: <span className="text-foreground font-semibold">{selectedFood} platos</span> + <span className="text-cyan-500 font-semibold">{selectedBev} bebidas</span>
                </span>
                {selectedBev > 0 && (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-amber-500" /> Bebidas se anaden al inventario
                  </span>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Processing / Complete */}
          {step === 3 && (
            <div className="text-center py-8 space-y-4">
              {result?.status === "completed" ? (
                <>
                  <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                  <h3 className="text-lg font-bold">Menu creado con exito</h3>
                  <div className="flex gap-3 justify-center">
                    <div className="bg-primary/10 rounded-lg p-3 text-center min-w-[80px]">
                      <p className="text-xl font-bold text-primary">{result.dishes_created}</p>
                      <p className="text-[10px] text-muted-foreground">Platos</p>
                    </div>
                    <div className="bg-cyan-500/10 rounded-lg p-3 text-center min-w-[80px]">
                      <p className="text-xl font-bold text-cyan-500">{result.beverages_created}</p>
                      <p className="text-[10px] text-muted-foreground">Bebidas</p>
                    </div>
                    <div className="bg-muted rounded-lg p-3 text-center min-w-[80px]">
                      <p className="text-xl font-bold">{result.categories_created}</p>
                      <p className="text-[10px] text-muted-foreground">Categorias</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Las bebidas tambien se han anadido al inventario con stock 0</p>
                </>
              ) : (
                <>
                  <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
                  <h3 className="text-lg font-bold">Creando menu y platos...</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedFood} platos + {selectedBev} bebidas en proceso
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button onClick={handleUpload} disabled={!file || uploading} data-testid="upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? "Analizando con IA..." : "Analizar archivo"}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Atras</Button>
              <Button onClick={handleConfirm} disabled={confirming || selectedItems.size === 0} data-testid="confirm-import-btn">
                {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                {onConfirmPreview ? `Confirmar ${selectedItems.size} items` : `Importar ${selectedItems.size} items`}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button className="ml-auto" onClick={handleClose} data-testid="close-import-btn">
              {result?.status === "completed" ? "Cerrar" : "Cerrar (sigue en background)"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
